import { Chip, tooltipClasses } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import { ReactNode, createElement, useMemo } from "react";
import { TbArrowRightDashed, TbBriefcase, TbClock, TbDoorExit, TbLogin2, TbRecycle } from "react-icons/tb";

import { TableHeader } from "@/components/TableHeader";
import { softColors } from "@/constants/colors";
import { NoMaxWidthTooltip } from "@/pages/DriverStatus.tsx/components/DriverStatusColumnTooltip";
import HotelStatusTooltip from "@/pages/HotelPlanning/arrival/components/table/components/HotelStatusTooltip";
import { CommonStatusItem } from "@/types/global";
import { User } from "@/types/user.types";

export const userLifecycleTooltips = {
    startedAt: {
        title: "İşe Giriş Tarihi",
        description: "Kullanıcının işe giriş tarihini temsil eder.",
    },
    leavedAt: {
        title: "İşten Çıkış Tarihi",
        description: "Kullanıcının işten çıkış tarihini temsil eder.",
    },
    active: {
        title: "Aktif Çalışan",
        description: "Kullanıcı aktif olarak çalışmaktadır.",
    },
};

export function LifeCycleDate(
    data: CommonStatusItem & { value: string | undefined; children?: ReactNode; fontSize?: number }
) {
    return (
        <NoMaxWidthTooltip
            arrow
            sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                    backgroundColor: "white",
                    border: "none",
                    padding: "0px !important",
                    fontSize: 12,
                    maxWidth: "200px !important",
                },
            }}
            title={<HotelStatusTooltip status={data}>{data.children}</HotelStatusTooltip>}
        >
            <Chip
                sx={{
                    backgroundColor: data.bg,
                    color: data.text,
                    fontWeight: 500,
                    fontSize: `${data.fontSize || 12}px !important`,
                }}
                icon={createElement(data.component(), {
                    fontSize: (data.fontSize && data.fontSize + 2) || 14,
                    color: data.text,
                })}
                label={data.value || "N/A"}
            />
        </NoMaxWidthTooltip>
    );
}

function RenderCell({ row }: { row: User }) {
    const activeLifeCycle = (row.lifecycles || []).at(-1);
    return (
        <div className="grid grid-cols-[133px,auto,133px] items-center py-1 pl-2 h-full ">
            <LifeCycleDate
                component={() => TbLogin2}
                value={
                    activeLifeCycle && activeLifeCycle.startedAt
                        ? DateTime.fromISO(activeLifeCycle.startedAt).toFormat("dd.MM.yyyy HH:mm")
                        : undefined
                }
                {...softColors.green}
                tooltip={userLifecycleTooltips.startedAt}
            />
            <TbArrowRightDashed fontSize={16} className="!shrink-0 self-center mx-auto !grow-0 " />
            <LeaveComponent
                leavedAt={
                    activeLifeCycle && activeLifeCycle.leavedAt ? DateTime.fromISO(activeLifeCycle.leavedAt) : undefined
                }
                startedAt={
                    activeLifeCycle && activeLifeCycle.startedAt
                        ? DateTime.fromISO(activeLifeCycle.startedAt)
                        : undefined
                }
            />
        </div>
    );
}

export function LeaveComponent({
    startedAt,
    leavedAt,
    fontSize,
}: {
    startedAt: DateTime | null | undefined;
    leavedAt: DateTime | null | undefined;
    fontSize?: number;
}) {
    return leavedAt ? (
        <LifeCycleDate
            fontSize={fontSize}
            component={() => TbDoorExit}
            value={leavedAt.toFormat("dd.MM.yyyy HH:mm")}
            {...softColors.brown}
            tooltip={userLifecycleTooltips.leavedAt}
        >
            <WorkingTime leavedAt={leavedAt} startedAt={startedAt} />
        </LifeCycleDate>
    ) : (
        <LifeCycleDate
            fontSize={fontSize}
            component={() => TbBriefcase}
            value={"Aktif Çalışan"}
            {...softColors.green}
            tooltip={userLifecycleTooltips.active}
        >
            <WorkingTime leavedAt={leavedAt} startedAt={startedAt} />
        </LifeCycleDate>
    );
}
const WorkingTime = ({
    startedAt,
    leavedAt,
}: {
    startedAt: DateTime | null | undefined;
    leavedAt: DateTime | null | undefined;
}) => {
    const workingDuration = useMemo(() => {
        if (!startedAt) return "";
        const endTime = leavedAt || DateTime.now();

        const diff = endTime.diff(startedAt, ["months", "days"]);

        return `${Math.floor(diff.months)} ay ${Math.round(diff.days)} gün`;
    }, [startedAt, leavedAt]);
    return (
        <div className=" text-sm  !p-0 flex flex-col    ">
            <div className="flex items-center gap-1 p-2   ">
                {createElement(TbBriefcase, {
                    fontSize: 14,
                })}
                Çalışma Süresi
            </div>
            <span className=" pb-2 px-2 pl-3 ">
                <Chip
                    sx={{
                        fontWeight: 500,
                        fontSize: "12px !important",
                    }}
                    icon={createElement(TbClock, {
                        fontSize: 14,
                    })}
                    label={workingDuration}
                />
            </span>
        </div>
    );
};

export const UserLifeCycleColumn = ({ t }: { t: TFunction }): GridColDef<User> => {
    return {
        field: "startedAt",
        headerName: t("userLifeCycle"),
        minWidth: 310,
        valueGetter: (_val, row) => (row.lifecycles || []).at(-1)?.startedAt,
        renderHeader: (params) => <TableHeader Component={TbRecycle} title={params.colDef.headerName!} />,
        renderCell: ({ row }) => <RenderCell row={row} />,
    };
};
export default UserLifeCycleColumn;
